import { SyncNotInitializedError, DuplicateKeyInBatchError, UpdateOperationItemNotFoundError, DeleteOperationItemNotFoundError } from "./errors.js";
const activeBatchContexts = /* @__PURE__ */ new WeakMap();
function normalizeOperations(ops, ctx) {
  const operations = Array.isArray(ops) ? ops : [ops];
  const normalized = [];
  for (const op of operations) {
    if (op.type === `delete`) {
      const keys = Array.isArray(op.key) ? op.key : [op.key];
      for (const key of keys) {
        normalized.push({ type: `delete`, key });
      }
    } else {
      const items = Array.isArray(op.data) ? op.data : [op.data];
      for (const item of items) {
        let key;
        if (op.type === `update`) {
          key = ctx.getKey(item);
        } else {
          const resolved = ctx.collection.validateData(
            item,
            op.type === `upsert` ? `insert` : op.type
          );
          key = ctx.getKey(resolved);
        }
        normalized.push({ type: op.type, key, data: item });
      }
    }
  }
  return normalized;
}
function validateOperations(operations, ctx) {
  const seenKeys = /* @__PURE__ */ new Set();
  for (const op of operations) {
    if (seenKeys.has(op.key)) {
      throw new DuplicateKeyInBatchError(op.key);
    }
    seenKeys.add(op.key);
    if (op.type === `update`) {
      if (!ctx.collection._state.syncedData.has(op.key)) {
        throw new UpdateOperationItemNotFoundError(op.key);
      }
    } else if (op.type === `delete`) {
      if (!ctx.collection._state.syncedData.has(op.key)) {
        throw new DeleteOperationItemNotFoundError(op.key);
      }
    }
  }
}
function performWriteOperations(operations, ctx) {
  const normalized = normalizeOperations(operations, ctx);
  validateOperations(normalized, ctx);
  ctx.begin({ immediate: true });
  for (const op of normalized) {
    switch (op.type) {
      case `insert`: {
        const resolved = ctx.collection.validateData(op.data, `insert`);
        ctx.write({
          type: `insert`,
          value: resolved
        });
        break;
      }
      case `update`: {
        const currentItem = ctx.collection._state.syncedData.get(op.key);
        const updatedItem = {
          ...currentItem,
          ...op.data
        };
        const resolved = ctx.collection.validateData(
          updatedItem,
          `update`,
          op.key
        );
        ctx.write({
          type: `update`,
          value: resolved
        });
        break;
      }
      case `delete`: {
        const currentItem = ctx.collection._state.syncedData.get(op.key);
        ctx.write({
          type: `delete`,
          value: currentItem
        });
        break;
      }
      case `upsert`: {
        const existsInSyncedStore = ctx.collection._state.syncedData.has(op.key);
        const resolved = ctx.collection.validateData(
          op.data,
          existsInSyncedStore ? `update` : `insert`,
          op.key
        );
        if (existsInSyncedStore) {
          ctx.write({
            type: `update`,
            value: resolved
          });
        } else {
          ctx.write({
            type: `insert`,
            value: resolved
          });
        }
        break;
      }
    }
  }
  ctx.commit();
  const updatedData = Array.from(ctx.collection._state.syncedData.values());
  if (ctx.updateCacheData) {
    ctx.updateCacheData(updatedData);
  } else {
    ctx.queryClient.setQueryData(ctx.queryKey, updatedData);
  }
}
function createWriteUtils(getContext) {
  function ensureContext() {
    const context = getContext();
    if (!context) {
      throw new SyncNotInitializedError();
    }
    return context;
  }
  return {
    writeInsert(data) {
      const operation = {
        type: `insert`,
        data
      };
      const ctx = ensureContext();
      const batchContext = activeBatchContexts.get(ctx);
      if (batchContext?.isActive) {
        batchContext.operations.push(operation);
        return;
      }
      performWriteOperations(operation, ctx);
    },
    writeUpdate(data) {
      const operation = {
        type: `update`,
        data
      };
      const ctx = ensureContext();
      const batchContext = activeBatchContexts.get(ctx);
      if (batchContext?.isActive) {
        batchContext.operations.push(operation);
        return;
      }
      performWriteOperations(operation, ctx);
    },
    writeDelete(key) {
      const operation = {
        type: `delete`,
        key
      };
      const ctx = ensureContext();
      const batchContext = activeBatchContexts.get(ctx);
      if (batchContext?.isActive) {
        batchContext.operations.push(operation);
        return;
      }
      performWriteOperations(operation, ctx);
    },
    writeUpsert(data) {
      const operation = {
        type: `upsert`,
        data
      };
      const ctx = ensureContext();
      const batchContext = activeBatchContexts.get(ctx);
      if (batchContext?.isActive) {
        batchContext.operations.push(operation);
        return;
      }
      performWriteOperations(operation, ctx);
    },
    writeBatch(callback) {
      const ctx = ensureContext();
      const existingBatch = activeBatchContexts.get(ctx);
      if (existingBatch?.isActive) {
        throw new Error(
          `Cannot nest writeBatch calls. Complete the current batch before starting a new one.`
        );
      }
      const batchContext = {
        operations: [],
        isActive: true
      };
      activeBatchContexts.set(ctx, batchContext);
      try {
        const result = callback();
        if (
          // @ts-expect-error - Runtime check for async callback, callback is typed as () => void but user might pass async
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          result && typeof result === `object` && `then` in result && // @ts-expect-error - Runtime check for async callback, callback is typed as () => void but user might pass async
          typeof result.then === `function`
        ) {
          throw new Error(
            `writeBatch does not support async callbacks. The callback must be synchronous.`
          );
        }
        if (batchContext.operations.length > 0) {
          performWriteOperations(batchContext.operations, ctx);
        }
      } finally {
        batchContext.isActive = false;
        activeBatchContexts.delete(ctx);
      }
    }
  };
}
export {
  createWriteUtils,
  performWriteOperations
};
//# sourceMappingURL=manual-sync.js.map
